import React, { useLayoutEffect, useRef, useState } from "react"
import { StudentTagInputInterface } from "../../../Interfaces/ComponentsInterfaces/SharedInterfaces"
import { StudentInterface } from "../../../Interfaces/Entities"
import { FilterStudentsForTag, FindTagQuery, GetStudentTagName, InsertTag, TagQuery } from "../../../Helpers/TagHelpers"
import "./StudentTagInput.css"

// Typing @ followed by a name opens a list of students; picking one writes @Name in the text.
function StudentTagInput({ value, onChange, students, multiline = false, placeholder,
	suggestionsPosition = 'below' }: StudentTagInputInterface) {

	const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
	const pendingCursor = useRef<number | undefined>(undefined)
	const [tagQuery, setTagQuery] = useState<TagQuery | undefined>(undefined)
	const [highlightedIndex, setHighlightedIndex] = useState<number>(0)

	const suggestions = tagQuery ? FilterStudentsForTag(students, tagQuery.query) : []
	const isShowingSuggestions = suggestions.length > 0

	useLayoutEffect(() => {
		const input = inputRef.current
		if (input && pendingCursor.current !== undefined) {
			input.focus()
			input.setSelectionRange(pendingCursor.current, pendingCursor.current)
			pendingCursor.current = undefined
		}
	}, [value.text])

	const updateTagQuery = (text: string, cursor: number) => {
		setTagQuery(FindTagQuery(text, cursor))
		setHighlightedIndex(0)
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const text = e.target.value
		onChange({ ...value, text })
		updateTagQuery(text, e.target.selectionStart ?? text.length)
	}

	const handleCursorMove = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const input = e.currentTarget
		updateTagQuery(input.value, input.selectionStart ?? input.value.length)
	}

	const selectStudent = (student: StudentInterface) => {
		const input = inputRef.current
		if (!tagQuery || !input) {
			return
		}
		const result = InsertTag(value, tagQuery, input.selectionStart ?? value.text.length, student)
		pendingCursor.current = result.cursor
		setTagQuery(undefined)
		onChange(result.value)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (!isShowingSuggestions) {
			return
		}
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setHighlightedIndex((highlightedIndex + 1) % suggestions.length)
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			setHighlightedIndex((highlightedIndex - 1 + suggestions.length) % suggestions.length)
		} else if (e.key === 'Enter' || e.key === 'Tab') {
			e.preventDefault()
			selectStudent(suggestions[Math.min(highlightedIndex, suggestions.length - 1)])
		} else if (e.key === 'Escape') {
			e.preventDefault()
			setTagQuery(undefined)
		}
	}

	const inputProps = {
		ref: inputRef,
		value: value.text,
		placeholder,
		onChange: handleChange,
		onKeyDown: handleKeyDown,
		onClick: handleCursorMove,
		onKeyUp: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
				handleCursorMove(e)
			}
		},
		onBlur: () => setTagQuery(undefined),
	}

	return (
		<div className="StudentTagInputDiv">
			{multiline ? <textarea {...inputProps} /> : <input type="text" {...inputProps} />}
			{isShowingSuggestions && (
				<ul className={`StudentTagSuggestions StudentTagSuggestions${suggestionsPosition === 'above' ?
					'Above' : 'Below'}`}>
					{suggestions.map((student, index) => (
						<li key={student.rut}
							className={index === highlightedIndex ? 'StudentTagSuggestionHighlighted' : ''}
							onMouseDown={(e) => {
								e.preventDefault()
								selectStudent(student)
							}}
							onMouseEnter={() => setHighlightedIndex(index)}>
							{GetStudentTagName(student)}
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

export default StudentTagInput
